/**
 * Parses expressions in a string, replacing ${variable} patterns with actual values.
 *
 * @remarks
 * This function supports:
 * - Simple variable replacement, e.g., `${name}`.
 * - Nested object references, e.g., `${user.profile.id}`.
 * - Mathematical operations, e.g., `${(quantity * price) * (1 - discount)}`.
 * - A mix of variables and literal numbers within expressions.
 *
 * @param expression The expression string containing patterns to be replaced.
 * @param formValues An object with key-value pairs for substitution.
 * @returns The parsed string with all patterns resolved and calculations performed.
 */
export const parseExpressions = (expression: string, formValues: Record<string, any>): string => {
  if (!expression || typeof expression !== 'string') {
    return expression
  }

  // Regex to match any content inside ${...}
  const regex = /\${([^}]+)}/g

  /**
   * Safely retrieves a value from a nested object using a dot-notation path.
   * @param obj The source object.
   * @param path The path to the desired value (e.g., "user.profile.id").
   * @returns The value at the specified path, or undefined if not found.
   */
  const getNestedValue = (obj: Record<string, any>, path: string): any => {
    if (typeof path !== 'string') return undefined
    const keys = path.split('.')
    let result = obj

    for (const key of keys) {
      if (result === undefined || result === null) return undefined
      result = result[key]
    }

    return result
  }

  // Find and replace all ${...} patterns in the expression string.
  return expression.replace(regex, (match: string, content: string) => {
    // Test if the content includes any mathematical operators.
    const hasOperators = /[\+\-\*\/\%\(\)]/.test(content)

    if (hasOperators) {
      try {
        // This regex finds all tokens that are either variable names or numbers.
        const preparedExpression = content.replace(/[a-zA-Z0-9_\.]+/g, (token: string) => {
          const trimmedToken = token.trim()

          // 1. Check if the token is a numeric literal (e.g., "100", "0.1").
          if (!isNaN(Number(trimmedToken))) {
            return trimmedToken // If it's a number, keep it as is.
          }

          // 2. If not a number, treat it as a variable name and get its value.
          const fieldValue = getNestedValue(formValues, trimmedToken)

          if (fieldValue !== undefined) {
            const numericValue = Number(fieldValue)
            // For safety, use 0 if the value is not a number.
            return !isNaN(numericValue) ? String(numericValue) : '0'
          }

          // If the variable is not found in formValues, default to 0 to prevent errors.
          return '0'
        })

        // Use the Function constructor for safe evaluation of the prepared expression.
        const result = new Function(`return ${preparedExpression}`)()
        return String(result)
      } catch (error) {
        console.error('Error evaluating expression:', content, error)
        return match // On error, return the original unparsed pattern.
      }
    } else {
      // If there are no operators, perform a simple value replacement.
      const trimmedFieldName = content.trim()
      const fieldValue = getNestedValue(formValues, trimmedFieldName)

      return fieldValue !== undefined ? String(fieldValue) : match
    }
  })
}
